import React from 'react';
import Container from '../components/Container';
import { useEffect, useState } from 'react';
import Meta from '../components/Meta';

const SkeletonCard = () => (
  <div className="bg-white rounded-lg overflow-hidden shadow-sm">
    <div className="relative pb-[56.25%] bg-gray-300"></div>
    <div className="p-3 sm:p-4">
      <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
    </div>
  </div>
);

const Videos = () => {
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchYoutubeVideos = async () => {
    try {
      setLoading(true);
      const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;

      let channelResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=contentDetails&forUsername=VedaEpicTales&key=${apiKey}`
      );
      let channelData = await channelResponse.json();

      if (!channelData.items || channelData.items.length === 0) {
        const searchResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/search?part=snippet&q=Veda+Epic+Tales&type=channel&maxResults=1&key=${apiKey}`
        );
        const searchData = await searchResponse.json();

        if (searchData.items && searchData.items.length > 0) {
          const channelId = searchData.items[0].snippet.channelId;
          channelResponse = await fetch(
            `https://www.googleapis.com/youtube/v3/channels?part=contentDetails&id=${channelId}&key=${apiKey}`
          );
          channelData = await channelResponse.json();
        }
      }

      if (!channelData.items || channelData.items.length === 0) {
        throw new Error('Channel not found');
      }

      const uploadsPlaylistId =
        channelData.items[0].contentDetails.relatedPlaylists.uploads;

      let allVideos = [];
      let nextPageToken = '';

      do {
        const playlistUrl = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails,status&playlistId=${uploadsPlaylistId}&maxResults=50&key=${apiKey}${nextPageToken ? `&pageToken=${nextPageToken}` : ''}`;

        const playlistResponse = await fetch(playlistUrl);
        const playlistData = await playlistResponse.json();

        if (!playlistData.items) {
          break;
        }

        const videos = playlistData.items
          .filter((item) => item.status.privacyStatus === 'public')
          .map((item) => ({
            id: item.contentDetails.videoId,
            title: item.snippet.title,
            thumbnail:
              item.snippet.thumbnails.high?.url ||
              item.snippet.thumbnails.medium?.url,
            channel: item.snippet.channelTitle,
            publishedAt: item.snippet.publishedAt,
            description: item.snippet.description,
          }));

        allVideos = [...allVideos, ...videos];
        nextPageToken = playlistData.nextPageToken || '';
      } while (nextPageToken);

      setYoutubeVideos(allVideos);
    } catch (error) {
      console.error('Error fetching YouTube videos:', error);
      const mockVideos = [
        {
          id: 'dQw4w9WgXcQ',
          title: 'Ramayana Episode 2: Lord Ram and Gurukul Learnings',
          thumbnail: 'https://via.placeholder.com/480x360',
          channel: 'Veda Epic Tales',
          publishedAt: new Date().toISOString(),
        },
        {
          id: 'dQw4w9WgXcQ',
          title: 'Mahabharat Episode 2 - Bheem vs Duryodhan',
          thumbnail: 'https://via.placeholder.com/480x360',
          channel: 'Veda Epic Tales',
          publishedAt: new Date().toISOString(),
        },
      ];
      setYoutubeVideos(mockVideos);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchYoutubeVideos();
  }, []);

  const openVideoModal = (video) => {
    setSelectedVideo(video);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    document.body.style.overflow = 'unset';
  };

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "VideoGallery",
    "name": "VEDA Epic Tales - Mythological Videos",
    "description": "Watch our collection of educational videos about Indian mythology, Ramayana, Mahabharata, and mythological stories for kids.",
    "url": "https://vedamerchandise.com/videos",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="VEDA Epic Tales - Mythological Videos for Kids"
        description="Watch our collection of educational videos about Indian mythology, Ramayana, Mahabharata, and mythological stories for kids."
        keywords="mythological videos, Indian mythology videos, educational videos, kids videos, VEDA videos, Ramayana videos, Mahabharata videos"
        author="VEDA Merchandise"
        ogTitle="VEDA Epic Tales - Mythological Videos for Kids"
        ogDescription="Watch our collection of educational videos about Indian mythology, Ramayana, Mahabharata, and mythological stories for kids."
        ogImage="https://vedamerchandise.com/videos-preview.jpg"
        ogUrl="https://vedamerchandise.com/videos"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="VEDA Epic Tales - Mythological Videos for Kids"
        twitterDescription="Watch our collection of educational videos about Indian mythology, Ramayana, Mahabharata, and mythological stories for kids."
        twitterImage="https://vedamerchandise.com/videos-preview.jpg"
        robots="index, follow"
        canonicalUrl="https://vedamerchandise.com/videos"
        schemaMarkup={schemaMarkup}
      />
      <div className="min-h-screen bg-gray-50">
        <Container>
          <div className="py-4 sm:py-8">
            {/* YouTube Videos Section */}
            <div className="px-4">
              <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 fredoka">
                Veda Epic Tales
              </h2>

              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <SkeletonCard key={idx} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {youtubeVideos.map((video) => (
                    <div
                      key={video.id}
                      className="bg-white rounded-lg overflow-hidden shadow-md cursor-pointer hover:shadow-lg"
                      onClick={() => openVideoModal(video)}
                    >
                      <div className="relative pb-[56.25%] h-0">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="absolute top-0 left-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-40 transition-all">
                          <svg
                            className="w-12 h-12 sm:w-16 sm:h-16 text-white"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                      <div className="p-3 sm:p-4">
                        <h3 className="font-medium text-sm sm:text-base line-clamp-2 fredoka">
                          {video.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">
                          {video.channel}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Container>

        {/* Video Modal */}
        {selectedVideo && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-60"
            onClick={closeVideoModal}
          >
            <div
              className="relative bg-white rounded-lg sm:rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden max-h-[95vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h3 className="text-sm sm:text-lg font-semibold text-gray-800 pr-2 sm:pr-4 flex-1 truncate fredoka">
                  {selectedVideo.title}
                </h3>
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() =>
                      window.open(
                        `https://www.youtube.com/watch?v=${selectedVideo.id}`,
                        '_blank'
                      )
                    }
                    className="px-2 sm:px-4 py-1.5 sm:py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs sm:text-sm font-medium rounded-md sm:rounded-lg transition-colors duration-200 whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Open in New Tab</span>
                    <span className="sm:hidden">Open</span>
                  </button>
                  <button
                    onClick={closeVideoModal}
                    className="px-3 sm:px-5 py-1.5 sm:py-2 bg-pink-500 hover:bg-pink-600 text-white text-xs sm:text-sm font-medium rounded-md sm:rounded-lg transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div
                className="relative bg-black flex-1"
                style={{ paddingBottom: '56.25%' }}
              >
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Videos;